import { FeatureCollection } from "geojson";
import { ListParams } from "../models/listParams";
import { OgcParams } from "../models/geo/ogcParams";

import { Discipline } from "../models/discipline";

import { BaseService } from "./base.service";
import { PlannenWebService } from "../webservices/plannen.web.service";
import { retryWithAuth } from "./utils/retryWithAuth";


export class PlannenService extends BaseService {
  // Private static instance of the class (singleton)
  private static instance: PlannenService;
  public disciplines: Discipline[] | null = null;
  public organisaties: any[] | null = null;
  public planStatusses: any[] | null = null;
  public planTypes: any[] | null = null;
  public beoordelingStatusses: any[] | null = null;

  // Private constructor to prevent direct instantiation
  private constructor() {
    super();
  }

  // Method to return the singleton instance
  public static getInstance(): PlannenService {
    if (!PlannenService.instance) {
      PlannenService.instance = new PlannenService();
    }
    return PlannenService.instance;
  }

  public async getPlan(id: string): Promise<any> {
    const planWebService = new PlannenWebService();

    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planWebService.getInfraPlanById(organisatie.id, id), 2)
    );

    // const organisatie = userManagementService.getSimulationOrganisatie();
    // if (organisatie && organisatie.id) {
    //   const planWebService = new PlannenWebService();

    //   try {
    //     return await planWebService.getInfraPlanById(organisatie.id, id);
    //   }
    //   catch (error: any) {
    //     // setError(error.message || 'Er is een fout opgetreden');
    //     try {
    //       // Try again
    //       await authService.initializeMsal();
    //       await authService.setTokenInStorage();
          
    //       return await planWebService.getInfraPlanById(organisatie.id, id);
    //     }
    //     catch (error: any) {
    //       throw error;
    //     }
    //   }
    // }
  }

  public async getPlannen(params: ListParams): Promise<any> {
    const planWebService = new PlannenWebService();
    
    if (!params) {
      params = {
        searchText: '',
        offset: 0,
        limit: 25,
        sortBy: 'Discipline',
        filter: ''
      };
    }

    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planWebService.getInfraPlannen(organisatie.id, params), 2, { redirectOnServerError: false })
    );
  }

  public async getPlannenFilters(): Promise<any> {
    const planWebService = new PlannenWebService();

    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planWebService.getInfraPlannenFilters(organisatie.id), 2)
    );
  }

  public async getPlannenOgcFeatures(ogcParams: OgcParams): Promise<FeatureCollection> {
    const planWebService = new PlannenWebService();

    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planWebService.getPlannenOgcFeaturesCollectionsItems(organisatie.id, ogcParams), 2, { redirectOnServerError: false })
    );

    // return {
    //   type: 'FeatureCollection',
    //   features: []
    // };
  }

  public getOrganisatiesSharedPlans(params: ListParams): Promise<any> {
    const planWebService = new PlannenWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planWebService.getOrganisatiesSharedPlans(organisatie.id, params), 2)
    );
  }

  public getSharedPlansByOrganisationId(sharedOrganisationId: string, params: ListParams): Promise<any> {
    const planWebService = new PlannenWebService();
    return this.getOrganisatieOrReject().then(organisatie =>
      retryWithAuth(() => planWebService.getSharedPlansByOrganisationId(organisatie.id, sharedOrganisationId, params), 2)
    );
  }



  /* Helper functions */

  public getInitials(account: any): string {
    const name = account.name || '';
    const [firstName, lastName] = name.split(' ');
    
    // Get the initials: first letter of first name and first letter of last name
    const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();

    return initials;
  }

  public async getDisciplines(): Promise<Discipline[]> {
    if (this.disciplines) {
      return this.disciplines;
    }

    const planWebService = new PlannenWebService();
    try {
      this.disciplines = await planWebService.getPlanDisciplines();
      //console.log(this.disciplines);
      // this.disciplines = this.disciplines.sort(this.helper.sortByOrder);
      return this.disciplines;
    }
    catch (error: any) {
      // setError(error.message || 'Er is een fout opgetreden');
    }

    return [];
  }

  public getDisciplineByName(name: string) {
    if (this.disciplines) {
      if (name) {
        const normalizedName = name.trim().toLowerCase();
        return this.disciplines.find(x => x.name.toLowerCase() === normalizedName);
      }

      return this.disciplines.find(x => x.name.toLowerCase() === 'onbekend');
    }

    return null;
  }

  public async getPlanStatusses(): Promise<any[]> {
    if (this.planStatusses) {
      return this.planStatusses;
    }

    const planWebService = new PlannenWebService();
    try {
      this.planStatusses = await planWebService.getPlanStatus();
      //console.log(this.statusses);
      return this.planStatusses;
    }
    catch (error: any) {
      // setError(error.message || 'Er is een fout opgetreden');
    }

    return [];
  }

  public async getPlanTypes(): Promise<any[]> {
    if (this.planTypes) {
      return this.planTypes;
    }

    const planWebService = new PlannenWebService();
    try {
      this.planTypes = await planWebService.getPlanTypes();
      //console.log(this.types);
      return this.planTypes;
    }
    catch (error: any) {
      // setError(error.message || 'Er is een fout opgetreden');
    }

    return [];
  }

  public async getPlanOrganisaties(): Promise<any[]> {
    if (this.organisaties) {
      return this.organisaties;
    }

    const planWebService = new PlannenWebService();
    try {
      this.organisaties = await planWebService.getPlanOrganisaties();
      //console.log(this.organisaties);
      return this.organisaties;
    }
    catch (error: any) {
      // setError(error.message || 'Er is een fout opgetreden');
    }

    return [];
  }

  public async getBeoordelingStatusses(): Promise<any[]> {
    if (this.beoordelingStatusses) {
      return this.beoordelingStatusses;
    }

    const planWebService = new PlannenWebService();
    try {
      this.beoordelingStatusses = await planWebService.getBeoordelingStatus();
      //console.log(this.statusses);
      return this.beoordelingStatusses;
    }
    catch (error: any) {
      // setError(error.message || 'Er is een fout opgetreden');
    }

    return [];
  }

};

// THIS IS THE KEY PART: 
// Export an instance of the PlanService class
export const plannenService = PlannenService.getInstance();